'use strict';

import { isMobile } from '../utilities/breakpoints';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const artisticSection = {
    toggleListener: null, // Used to store the mobile toggle handler for cleanup on resize

    init() {
        const section = document.querySelector('.artistic-section');
        if (!section) return;

        if (isMobile()) {
            this.initMobile(section);
        } else {
            this.initDesktop(section);
        }
    },

    // ----- Mobile behavior: swap images on toggle click
    initMobile(section) {
        const toggle = section.querySelector('.artistic-section__toggle');
        const mainImg = section.querySelector('.artistic-section__main-img img');
        const overlayImg = section.querySelector('.artistic-section__overlay-img img');
        const mainSources = section.querySelectorAll('.artistic-section__main-img source');
        const overlaySources = section.querySelectorAll('.artistic-section__overlay-img source');


        if (!toggle || !mainImg || !overlayImg || !mainSources || !overlaySources) return;
        if (mainSources.length !== overlaySources.length) return;

        this.toggleListener = () => {
            // Swap <source> srcset and data-srcset
            mainSources.forEach((mainSource, i) => {
                const overlaySource = overlaySources[i];
                [mainSource.srcset, overlaySource.srcset] = [overlaySource.srcset, mainSource.srcset];
                [mainSource.dataset.srcset, overlaySource.dataset.srcset] = [overlaySource.dataset.srcset, mainSource.dataset.srcset];
            });

            // Swap <img> src and data-src
            [mainImg.src, overlayImg.src] = [overlayImg.src, mainImg.src];
            [mainImg.dataset.src, overlayImg.dataset.src] = [overlayImg.dataset.src, mainImg.dataset.src];
        };

        toggle.addEventListener('click', this.toggleListener);
    },

    // ----- Desktop behavior: GSAP scroll animation
    initDesktop(section) {
        const mainImg = section.querySelector('.artistic-section__main-img');
        const innerImg = mainImg.querySelector('img');
        const overlay = section.querySelector('.artistic-section__overlay');

        if (!mainImg || !overlay) return;

        // Set initial styles for animation
        gsap.set(mainImg, { width: '100%' });
        gsap.set(overlay, { yPercent: 100, opacity: 0 });
        gsap.set(overlay, {
            xPercent: -100, // shifts overlay left so ~25% of it overlaps the right edge of the main image
            yPercent: 100,
            opacity: 0
        });
        gsap.set(innerImg, { objectPosition: 'top' });

        // Scroll animation
        ScrollTrigger.create({
            trigger: section,
            start: 'top 60%',
            end: 'bottom top',
            onEnter: () => {
                // Shrink image and recenter inner <img>
                gsap.to(mainImg, {
                    width: '60%',
                    duration: 2,
                    ease: 'power4.out'
                });

                // Slide in overlay with fade
                gsap.to(overlay, {
                    yPercent: -50,
                    xPercent: -100,
                    opacity: 1,
                    duration: 1,
                    ease: 'power4.out',
                    delay: 0.8
                });

                // Shift focal point of image to center
                gsap.to(innerImg, {
                    objectPosition: 'center',
                    duration: 1,
                    ease: 'power4.out',
                    delay: 0.8
                });
            },
            onLeaveBack: () => {
                // Restore original image size
                gsap.to(mainImg, {
                    width: '100%',
                    duration: 1,
                    ease: 'power2.inOut'
                });

                // Slide out overlay and fade
                gsap.to(overlay, {
                    yPercent: 100,
                    opacity: 0,
                    duration: 0.3,
                    ease: 'power2.in',
                    delay: 0.3
                });

                // Reset focal point to top
                gsap.to(innerImg, {
                    objectPosition: 'top',
                    duration: 0.3,
                    ease: 'power4.out',
                    delay: 0.3
                });
            }
        });
    }
};

artisticSection.init();

// ----- Handle layout switch on resize
let currentIsMobile = isMobile();

window.addEventListener('resize', () => {
    const nowIsMobile = isMobile();
    if (nowIsMobile !== currentIsMobile) {
        currentIsMobile = nowIsMobile;

        // Clean all GSAP triggers
        ScrollTrigger.getAll().forEach(trigger => trigger.kill());

        // Reset all inline styles
        const section = document.querySelector('.artistic-section');
        if (section) {
            section.querySelectorAll('[style]').forEach(el => el.removeAttribute('style'));
        }

        // Remove listener and reset swapped images if needed
        const toggle = document.querySelector('.artistic-section__toggle');
        if (toggle && artisticSection.toggleListener) {
            toggle.removeEventListener('click', artisticSection.toggleListener);
            artisticSection.toggleListener = null;

            const mainImg = section.querySelector('.artistic-section__main-img img');
            const overlayImg = section.querySelector('.artistic-section__overlay-img img');

            if (mainImg && overlayImg && mainImg.src !== mainImg.dataset.initialSrc) {
                [mainImg.src, overlayImg.src] = [overlayImg.src, mainImg.src];
                [mainImg.dataset.src, overlayImg.dataset.src] = [overlayImg.dataset.src, mainImg.dataset.src];
            }
        }

        // Re-initialize correct behavior
        document.addEventListener('DOMContentLoaded', () => {
            artisticSection.init();
        });

    }
});

